/**
 * The buyer's view of their dining credit.
 *
 * Everything here is pure so it can be reasoned about without a browser: the page renders these
 * figures and never computes its own.
 */

/**
 * Whether to show the guest anything about dining at all — the header entry included.
 *
 * The bootstrap sends `dining: null` both for a guest who was never enrolled and for one whose
 * credit has been switched off, deliberately, so a credit figure never reaches someone who cannot
 * spend it. `isActive` is checked as well rather than trusting the object's presence: if the shape
 * ever gains an inactive variant, a truthiness test would quietly start leaking a balance.
 */
export const hasDiningAccess = (dining) => Boolean(dining && dining.isActive === true);

const toNumber = (value) => {
    const n = Number(value ?? 0);
    return Number.isFinite(n) ? n : 0;
};

/**
 * The three figures on the balance card.
 *
 * `spent` is DERIVED — `monthlyAmount - balance` — and is never summed from the meal list. The
 * list is paginated, so a client-side total is wrong until every page has loaded, and it would be
 * wrong in the most damaging way: quietly, and next to a balance it disagrees with. This is the
 * same trap that made the admin tab's "Total Spent" (which read `orderTotal`) fail to add up
 * against a balance computed from `amount`. Two server numbers in, one answer out, correct on the
 * first page.
 *
 * A negative balance is overage, which is allowed by design and nothing clamps server-side. It is
 * reported as its own flag with a positive `overageAmount`, because "-120" in a sentence reads as
 * a bug while "120 over" reads as a fact.
 */
export const getDiningSummary = ({ monthlyAmount, balance } = {}) => {
    const grant = toNumber(monthlyAmount);
    const remaining = toNumber(balance);
    const spent = grant - remaining;
    const isOverdrawn = remaining < 0;

    // The bar shows how much of the grant is gone. Clamped at both ends: it must not invert on an
    // over-refunded balance, and overage fills the track rather than overflowing it.
    const usedPercent = grant > 0 ? Math.min(100, Math.max(0, (spent / grant) * 100)) : 0;

    return {
        monthlyAmount: grant,
        remaining,
        spent,
        isOverdrawn,
        overageAmount: isOverdrawn ? Math.abs(remaining) : 0,
        usedPercent,
    };
};

/**
 * Meals bucketed by calendar month, for the list's sticky headers.
 *
 * Server order is preserved rather than re-sorted: the endpoint sorts newest-first by `visitDate`,
 * and re-sorting locally over a paginated list would reorder only the pages already loaded.
 *
 * A commit with no `visitDate` gets an `unknown` bucket instead of being dropped or dated to the
 * epoch — a meal that exists should be listed even when its timestamp did not survive the POS.
 */
export const groupCommitsByMonth = (commits) => {
    if (!Array.isArray(commits)) return [];

    const groups = [];

    commits.forEach((commit) => {
        const date = commit?.visitDate ? new Date(commit.visitDate) : null;
        const valid = date && !Number.isNaN(date.getTime());
        const key = valid
            ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
            : 'unknown';

        // Grouped by RUNS of the server's order, not collected by key across the whole list. A
        // key-indexed map merged every undated meal into whichever group the first one created,
        // so [August, undated A, July, undated B] rendered as [August, undated A, undated B, July]
        // — silently moving a meal ahead of one it came after. A repeated key now opens a second
        // group, which is honest: it shows the order the server actually sent.
        const currentGroup = groups[groups.length - 1];
        if (!currentGroup || currentGroup.key !== key) {
            groups.push({ key, date: valid ? date : null, commits: [] });
        }
        groups[groups.length - 1].commits.push(commit);
    });

    return groups;
};

/**
 * Whether a page request may start.
 *
 * The infinite-scroll observer holds a stale `page` while a request is in flight — `loading` only
 * covers the first load — so a sentinel that leaves and re-enters the viewport fires again for the
 * same page and appends its commits twice. Extracted so the rule is testable without a DOM.
 */
export const shouldStartPageLoad = ({ inFlightPage, nextPage }) => inFlightPage !== nextPage;
